import { AttentionIssue } from "client/openapi/models/AttentionIssue";
import { PersonKind } from "client/openapi/models/PersonKind";
import type { PersonRow } from "client/openapi/models/PersonRow";
import type { TaskDue } from "client/openapi/models/TaskDue";
import { PeopleService } from "client/openapi/services/PeopleService";
import PersonPill, { PillColor } from "components/PersonPill";
import "./index.css";

const KIND_LABELS: Record<PersonKind, string> = {
  [PersonKind.STUDENT]: "Student",
  [PersonKind.PARENT]: "Parent",
  [PersonKind.TUTOR]: "Tutor",
};

const KIND_COLORS: Record<PersonKind, PillColor> = {
  [PersonKind.STUDENT]: "green",
  [PersonKind.PARENT]: "blue",
  [PersonKind.TUTOR]: "orange",
};

/** Unique across kinds: a student and a tutor can share an id. */
export function personKey(person: Pick<PersonRow, "kind" | "id">): string {
  return `${person.kind}-${person.id}`;
}

/** A row or a family member. */
type Person = Pick<PersonRow, "kind" | "id" | "first_name" | "last_name">;

export function personName(person: Person): string {
  return `${person.first_name ?? ""} ${person.last_name ?? ""}`.trim();
}

/** /manage/students/:id, /manage/parents/:id or /manage/tutors/:id */
export function profilePath(person: Person): string {
  return `/manage/${person.kind}s/${person.id}`;
}

/** The line under a name: phone, else email, else the parent a student is reached through. */
export function personDetail(person: PersonRow): string | null {
  if (person.phone_number) {
    // Numbers are stored as +1XXXXXXXXXX; anything else shows as stored.
    const us = person.phone_number.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
    return us ? `(${us[1]}) ${us[2]}-${us[3]}` : person.phone_number;
  }
  if (person.email) return person.email;
  if (person.kind === PersonKind.STUDENT && person.family.length > 0) {
    return `via ${personName(person.family[0])}`;
  }
  return null;
}

export function KindChip({ kind }: { kind: PersonKind }) {
  return <PersonPill label={KIND_LABELS[kind]} color={KIND_COLORS[kind]} />;
}

/** Dismisses tasks due at their due dates as shown, all at once. The tasks
 * stay as they are. */
export function dismissTasks(orgId: number, tasks: TaskDue[]) {
  return PeopleService.dismissTasks({
    orgId,
    requestBody: tasks.map((task) => ({
      task_id: task.id,
      fingerprint: task.fingerprint,
    })),
  });
}

/** Dismisses each of the row's issues as shown (a task due: each of its
 * tasks). */
export function dismissIssues(orgId: number, row: PersonRow) {
  return Promise.all(
    row.issues.map((issue) =>
      issue === AttentionIssue.TASK_DUE
        ? dismissTasks(orgId, row.tasks ?? [])
        : PeopleService.dismissAttention({
            orgId,
            requestBody: {
              kind: row.kind,
              id: row.id,
              issue,
              fingerprint: row.fingerprints[issue],
            },
          })
    )
  );
}
